// Time math for Callboard. All wall-clock reasoning happens in America/Detroit;
// all overlap/duration reasoning happens on true UTC instants so DST weeks are
// correct (the November overnight shift really is 13h, March's 11h).
import { DateTime } from 'luxon'

export const TZ = 'America/Detroit'

/** YYYY-MM-DD of the Sunday starting the week that contains `date` (local). */
export function weekKeyFor(date: string): string {
  const dt = DateTime.fromISO(date, { zone: TZ })
  if (!dt.isValid) throw new Error(`Invalid date: ${date}`)
  // Luxon weekday: 1 = Monday … 7 = Sunday. Sunday-start week.
  const daysSinceSunday = dt.weekday % 7
  return dt.minus({ days: daysSinceSunday }).toISODate()!
}

/** The 7 dates (YYYY-MM-DD) of the week starting at `weekStart` (a Sunday). */
export function weekDates(weekStart: string): string[] {
  const start = DateTime.fromISO(weekStart, { zone: TZ })
  return Array.from({ length: 7 }, (_, i) => start.plus({ days: i }).toISODate()!)
}

export function addDays(date: string, n: number): string {
  return DateTime.fromISO(date, { zone: TZ }).plus({ days: n }).toISODate()!
}

export function todayLocal(): string {
  return DateTime.now().setZone(TZ).toISODate()!
}

export interface ShiftClock {
  date: string
  startTime: string
  endTime: string
  endsNextDay: boolean
}

/** Convert local wall-clock shift fields to true UTC instants (ISO strings). */
export function toInstants(clock: ShiftClock): { startAt: string; endAt: string } {
  const start = DateTime.fromISO(`${clock.date}T${clock.startTime}`, { zone: TZ })
  const endDate = clock.endsNextDay ? addDays(clock.date, 1) : clock.date
  const end = DateTime.fromISO(`${endDate}T${clock.endTime}`, { zone: TZ })
  if (!start.isValid || !end.isValid) {
    throw new Error(`Invalid shift clock: ${JSON.stringify(clock)}`)
  }
  return { startAt: start.toUTC().toISO()!, endAt: end.toUTC().toISO()! }
}

/** Paid duration in hours, DST-correct, minus unpaid break. Rounded to 2 places. */
export function durationHours(startAt: string, endAt: string, breakMinutes = 0): number {
  const ms = Date.parse(endAt) - Date.parse(startAt)
  const hours = ms / 3_600_000 - breakMinutes / 60
  return Math.round(hours * 100) / 100
}

/** True when an end time at-or-before the start time implies the shift crosses midnight. */
export function impliesNextDay(startTime: string, endTime: string): boolean {
  return endTime <= startTime
}

export function isValidHHmm(s: string): boolean {
  return /^([01]\d|2[0-3]):[0-5]\d$/.test(s)
}

export function isValidISODate(s: string): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(s) && DateTime.fromISO(s, { zone: TZ }).isValid
}

/** Weekday key ('sun'…'sat') for a local date. */
export function weekdayKey(date: string): 'sun' | 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' {
  const keys = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const
  return keys[DateTime.fromISO(date, { zone: TZ }).weekday - 1]
}

/** Index 0 (Sunday) … 6 (Saturday) for a local date. */
export function dayOfWeek(date: string): number {
  return DateTime.fromISO(date, { zone: TZ }).weekday % 7
}
